import {
  AthleteDraftWorkoutDomain,
  AthleteModifiedPublishedWorkoutDomain,
  BenchmarkGrid,
  BlockType,
  Metric,
  PublishedWorkoutStatusDomain,
  WorkoutLayerDomain,
} from '@strength/domain';
import { DefaultWeightUnit } from '@strength/db-schema';
import {
  MetricBenchmarkData,
  ExerciseMutationData,
  exerciseToTrainingLabel,
  getWorkoutExerciseIds,
  HasWorkoutLayer,
  mergeAthleteWorkoutLayers,
  SetMutationData,
  Workout,
  WorkoutAlternateGroup,
  WorkoutExercise,
  WorkoutSet,
  WorkoutSuperset,
} from '@strength/domain-workout';
import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { v4 } from 'uuid';
import { useMutation } from '../../../../../loco-sync';
import { trpc } from '../../../../../trpc/react';

import { useExerciseBenchmarks } from '../../../../hooks/useExerciseBenchmarks';
import { useTabletBenchmarks } from '../../../../hooks/useTabletBenchmarks';
import { useTabletMetrics } from '../../../../hooks/useTabletMetrics';
import { useMetrics } from '../../../../hooks/useMetrics';
import { toast } from '../../../../hooks/useToast';
import { WorkoutLayerCommand } from '../../../block/mutation-fns/types';
import {
  ActualWorkoutLayerMutationFns,
  createActualMutationFns,
} from '../../actuals-mutations';
import {
  makeModifiedPublishedWorkoutLayerMutationFns,
  ModifiedLayerCallbacks,
  ModifiedPublishedWorkoutLayerMutationsFns,
  runAthleteModifiedPublishedMutation,
} from '../../modified-published-mutations';
import { SectionExercises, WorkoutMutationContext } from '../../types';
import { getExercisesFromEntry } from '../../utils';
import { TabletCreateEditExerciseSheet } from '../components/create-edit-exercise-sheet/TabletCreateEditExerciseSheet';
import {
  ExerciseFormData,
  MetricData,
  SetData,
} from '../components/create-edit-exercise-sheet/types';
import { CompleteWorkoutDialog } from '../components/dialogs/CompleteWorkoutDialog';
import { TabletExerciseNoteModal } from '../components/dialogs/TabletExerciseNoteModal';
import { ExerciseHistoryDrawer } from '../components/drawers/ExerciseHistoryDrawer';
import {
  getIsEachSideEdits,
  getMetricsEdits,
  getSetsEdits,
  parseExerciseData,
} from '../edit-exercise-utils';
import { getUnrecordedActuals } from '../group-workout-utils';
import {
  AthleteWithTeam,
  TabletExerciseFilters,
  UnrecordedActual,
  WorkoutData,
} from '../types';
import { useTabletContext } from './TabletContext';

export interface WorkoutContextValue {
  selectedAthlete: AthleteWithTeam;
  workout: Workout | undefined;
  originalWorkout: Workout | undefined;
  context: WorkoutMutationContext;
  exerciseLabelMap: Record<string, string>;
  exerciseVideoMap: Record<string, string>;
  applyBenchmarkData: MetricBenchmarkData;
  // Resolves a benchmark grid by id across ALL selected teams' grids. Exposed so
  // per-exercise components (e.g. SectionExercises) resolve benchmark badge names
  // from the multi-team result rather than a single team's grids.
  getBenchmarkGrid: (id: string) => BenchmarkGrid | null;
  // Weight unit resolved for THIS provider's `selectedAthlete.teamId`, so each
  // athlete in a mixed-team selection uses their OWN team's unit rather than
  // collapsing to one team. Exposed for the athlete-scoped tablet inputs.
  defaultWeightUnit: DefaultWeightUnit;
  getUnitsForMetric: (metricId: string, forActuals?: boolean) => string[];
  // Org-scoped metric lookup (not team-specific) — exposed so the tablet inputs
  // get every metric helper from one place rather than also reaching for useMetrics.
  getMetric: (id: string) => Metric;
  actualLayer: HasWorkoutLayer | null;
  // Exposed so condensed-shorthand formatting (getShorthandMetrics) can special-case
  // bowling blocks, whose deliveries are shoehorned into the sets/reps model — see SC-4109.
  blockType: BlockType | undefined;
  hasNextSection: boolean;
  handleNextSection: () => void;
  handleOpenAddExerciseDrawer: (exercise?: WorkoutExercise) => void;
  handleOpenAddNoteExerciseDrawer: (exercise?: WorkoutExercise) => void;
  handleEditSet: (
    workoutId: string,
    workoutSetId: string,
    exercise: WorkoutExercise,
    data: SetMutationData,
  ) => void;
  handleEditExerciseStatus: (
    workoutId: string,
    workoutExerciseId: string,
    sets: WorkoutSet[],
    data: ExerciseMutationData,
  ) => void;
  handleOpenExerciseHistoryDrawer: (exercise?: WorkoutExercise) => void;
  handleOpenCompleteWorkoutDialog: () => void;
  handleCompleteWorkout: () => void;

  // This is just for the tablet create edit exercise sheet
  // Tech Debt: clean up TabletCreateEditExerciseSheet to not need this once V1 tablet is deprecated
  sectionExercises: SectionExercises[];

  // filters
  filters: TabletExerciseFilters;

  // mutations
  modifiedPublishedMutationsFns: ModifiedPublishedWorkoutLayerMutationsFns;
  actualMutationFns: ActualWorkoutLayerMutationFns;
  unrecordedActuals: UnrecordedActual[];
}

const WorkoutContext = createContext<WorkoutContextValue | undefined>(
  undefined,
);

export interface WorkoutProviderProps {
  children: React.ReactNode;
  athleteWorkoutLayers: WorkoutData;
  selectedAthlete: AthleteWithTeam;
  filters: TabletExerciseFilters;
}

export const WorkoutProvider: React.FC<WorkoutProviderProps> = ({
  children,
  athleteWorkoutLayers,
  selectedAthlete,
  filters,
}) => {
  const [addExerciseDrawerOpen, setAddExerciseDrawerOpen] = useState(false);
  const [addNoteExerciseDrawerOpen, setAddNoteExerciseDrawerOpen] =
    useState(false);
  const [exerciseHistoryDrawerOpen, setExerciseHistoryDrawerOpen] =
    useState(false);
  const [completeWorkoutDrawerOpen, setCompleteWorkoutDrawerOpen] =
    useState(false);
  const [selectedExercise, setSelectedExercise] = useState<WorkoutExercise>();
  const {
    setActiveAthletes,
    onSelectSection,
    getSelectedSectionId,
    onSelectWorkout,
    selectedAthletes,
    canShowSplitView,
    splitViewEnabled,
    removeAthleteFromSelectedAthletes,
    onExit,
  } = useTabletContext();
  const { t } = useTranslation();

  const mutation = useMutation();

  const updateActivityLog = trpc.activityLog.updateActivityLog.useMutation();
  const addExerciseNote = trpc.tablet.addExerciseNote.useMutation();
  const { setsMetric, getMetric } = useMetrics();

  // The tablet collection screen records actuals for athletes across MULTIPLE
  // teams at once. Resolve benchmark grids against the UNION of selected teams
  // (matching the `tw-team-ids` header scope) so each athlete's grids come from
  // their OWN team rather than collapsing to one arbitrary team. A single shared
  // fetch is keyed off this `teamIds` array across all providers.
  const teamIds = useMemo(
    // sorted so the same team set yields one stable react-query cache key
    () => [...new Set(selectedAthletes.map((a) => a.teamId))].sort(),
    [selectedAthletes],
  );
  const { getDefaultBenchmarkGridId, getBenchmarkGrid } =
    useTabletBenchmarks(teamIds);
  const defaultBenchmarkGridId = getDefaultBenchmarkGridId(
    selectedAthlete.teamId,
  );

  // Resolve the weight unit per team (same shared fetch keyed off `teamIds`), then
  // bind to THIS provider's athlete so each athlete renders in their own team's unit.
  // Destructure the stable callbacks so the binding below doesn't re-create every render.
  const { getDefaultWeightUnit, getUnitsForMetric: getUnitsForMetricForTeam } =
    useTabletMetrics(teamIds);
  const defaultWeightUnit = getDefaultWeightUnit(selectedAthlete.teamId);
  const getUnitsForMetric = useCallback(
    (metricId: string, forActuals?: boolean) =>
      getUnitsForMetricForTeam(metricId, selectedAthlete.teamId, forActuals),
    [getUnitsForMetricForTeam, selectedAthlete.teamId],
  );

  const rawWorkout = useMemo(() => {
    return mergeAthleteWorkoutLayers(
      athleteWorkoutLayers.blockLayer,
      athleteWorkoutLayers,
      athleteWorkoutLayers.modifier,
      athleteWorkoutLayers.actual,
    );
  }, [athleteWorkoutLayers]);

  // Pre-apply exerciseFilters so consumers receive a workout with non-matching
  // exercises already removed. Sections with no remaining entries are dropped too.
  const workout = useMemo(() => {
    if (!rawWorkout) return undefined;
    if (filters.exerciseFilters.length === 0) return rawWorkout;
    const filterIds = new Set(filters.exerciseFilters.map((f) => f.id));
    const filteredSections = rawWorkout.sections
      .map((section) => ({
        ...section,
        entries: section.entries.flatMap(
          (
            entry,
          ): (WorkoutExercise | WorkoutSuperset | WorkoutAlternateGroup)[] => {
            if (entry.__type === 'WorkoutExercise') {
              return filterIds.has(entry.libraryExerciseId) ? [entry] : [];
            }
            if (entry.__type === 'WorkoutSuperset') {
              const filteredExercises = entry.exercises.filter((se) =>
                filterIds.has(se.exercise.libraryExerciseId),
              );
              return filteredExercises.length > 0
                ? [{ ...entry, exercises: filteredExercises }]
                : [];
            }
            if (entry.__type === 'WorkoutAlternateGroup') {
              const filteredExercises = entry.exercises.filter((ae) =>
                filterIds.has(ae.exercise.libraryExerciseId),
              );
              const [firstExercise] = filteredExercises;
              if (!firstExercise) return [];
              const selectedExerciseId = filteredExercises.some(
                (ae) => ae.workoutExerciseId === entry.selectedExerciseId,
              )
                ? entry.selectedExerciseId
                : firstExercise.workoutExerciseId;
              return [
                { ...entry, exercises: filteredExercises, selectedExerciseId },
              ];
            }
            return [];
          },
        ),
      }))
      .filter((section) => section.entries.length > 0);
    return { ...rawWorkout, sections: filteredSections };
  }, [rawWorkout, filters.exerciseFilters]);

  const originalWorkout = useMemo(() => {
    return mergeAthleteWorkoutLayers(
      athleteWorkoutLayers.blockLayer,
      athleteWorkoutLayers,
      athleteWorkoutLayers.modifier,
    );
  }, [athleteWorkoutLayers]);

  // Use rawWorkout so filters don't affect completion tracking
  const unrecordedActuals = useMemo(
    () => getUnrecordedActuals(rawWorkout),
    [rawWorkout],
  );

  const context = useMemo(() => {
    return {
      blockId: athleteWorkoutLayers.blockId,
      profileId: athleteWorkoutLayers.profileId,
      athleteVersion: athleteWorkoutLayers.version,
      // If the block layer is changed on an in-progress workout, we don't create a new
      // athlete layer, so the existing one still points to the old block version. The
      // modified layer will point to the correct version
      blockVersion:
        athleteWorkoutLayers.modifier?.blockVersion ??
        athleteWorkoutLayers.blockVersion,
      blockLayerId: athleteWorkoutLayers.blockLayer?.id,
      athleteLayerId: athleteWorkoutLayers.id,
      actualLayerId: athleteWorkoutLayers?.actual?.id ?? '',
      workoutStatus: athleteWorkoutLayers.status,
    };
  }, [athleteWorkoutLayers]);

  const exerciseLabelMap = useMemo(() => {
    if (!rawWorkout) return {};
    return exerciseToTrainingLabel(rawWorkout);
  }, [rawWorkout]);

  // Query max conversions for all library exercise IDs in the workout
  const workoutLibraryExerciseIds = useMemo(() => {
    return rawWorkout ? getWorkoutExerciseIds(rawWorkout) : [];
  }, [rawWorkout]);

  // Fetch video URLs for all exercises in the workout
  const { data: exerciseVideosData } =
    trpc.exerciseLibrary.exerciseVideosByIds.useQuery(
      { libraryExerciseIds: workoutLibraryExerciseIds },
      {
        enabled: workoutLibraryExerciseIds.length > 0,
      },
    );

  const exerciseVideoMap = useMemo(() => {
    if (!exerciseVideosData) return {};
    return exerciseVideosData.reduce(
      (acc, { id, videoUrl }) => {
        if (videoUrl) {
          acc[id] = videoUrl;
        }
        return acc;
      },
      {} as Record<string, string>,
    );
  }, [exerciseVideosData]);

  const {
    exerciseBenchmarks,
    conversions,
    isLoading: isExerciseBenchmarksLoading,
  } = useExerciseBenchmarks({
    libraryExerciseIds: workoutLibraryExerciseIds,
    profileId: context.profileId,
  });

  // KPI-mapped metric columns resolve their `%` to this athlete's targets
  // (SC-3844). A tablet `multiTeamProcedure` scoped over the `tw-team-ids`
  // header, taking the athlete's own teamId because the tablet spans athletes
  // from multiple teams.
  const { data: kpiBenchmarks } = trpc.tablet.kpiBenchmarksForAthlete.useQuery(
    { profileId: selectedAthlete.id, teamId: selectedAthlete.teamId },
    { staleTime: 10_000 },
  );

  const applyBenchmarkData: MetricBenchmarkData = useMemo(
    () => ({
      currentExerciseBenchmarks: isExerciseBenchmarksLoading
        ? []
        : exerciseBenchmarks,
      maxConversions: isExerciseBenchmarksLoading ? [] : conversions,
      trainingMaxRatio: athleteWorkoutLayers.block?.trainingMaxRatio ?? 1,
      defaultBenchmarkGridId,
      kpiBenchmarks: kpiBenchmarks ?? {},
    }),
    [
      isExerciseBenchmarksLoading,
      exerciseBenchmarks,
      conversions,
      athleteWorkoutLayers.block?.trainingMaxRatio,
      defaultBenchmarkGridId,
      kpiBenchmarks,
    ],
  );

  const actualMutationFns = useMemo(() => {
    return createActualMutationFns({
      actualLayer: athleteWorkoutLayers.actual,
      blockLayer: athleteWorkoutLayers.blockLayer,
      modifierLayer: athleteWorkoutLayers.modifier,
      athleteLayer: athleteWorkoutLayers,
      mutation,
    });
  }, [athleteWorkoutLayers, mutation]);

  const modifiedPublishedMutationsFns = useMemo(() => {
    const modifiedPublishedLayers = athleteWorkoutLayers.modifier
      ? [athleteWorkoutLayers.modifier]
      : [];

    const athleteDraftLayers = athleteWorkoutLayers.draft
      ? [athleteWorkoutLayers.draft]
      : [];

    const blockDraftLayers = athleteWorkoutLayers.blockDraft
      ? [athleteWorkoutLayers.blockDraft]
      : [];

    const bulkMutate = (
      commands: readonly AthleteModifiedPublishedWorkoutDomain.Command[],
      callbacks?: ModifiedLayerCallbacks,
    ) => {
      commands.forEach((c) => mutation.mutate(c, callbacks));
    };

    const runCommands = (
      context: WorkoutMutationContext,
      command: WorkoutLayerCommand,
      draftCommand: WorkoutLayerCommand,
      callbacks?: ModifiedLayerCallbacks,
    ) => {
      runAthleteModifiedPublishedMutation(context, [command], (commands) =>
        bulkMutate(commands, callbacks),
      );

      const draftEdits = draftCommand.edits as AthleteDraftWorkoutDomain.Edit[];
      const { exists, id, workoutId } = draftCommand.identity;

      if (!exists) {
        // No existing athlete draft layer; create it
        const blockLayer = blockDraftLayers.find(
          (l) => l.workoutId === workoutId,
        );
        draftEdits.unshift({
          action: 'create',
          modelName: 'AthleteDraftWorkoutLayer',
          modelId: id,
          data: {
            name: blockLayer?.name ?? undefined,
            cycleSortOrder: athleteWorkoutLayers.cycleSortOrder,
            sessionSortOrder: athleteWorkoutLayers.sessionSortOrder,
            layer: WorkoutLayerDomain.defaultLayer(),
          },
        });
      }

      const athleteDraftCommand: AthleteDraftWorkoutDomain.Command = {
        module: AthleteDraftWorkoutDomain.Module,
        identity: {
          blockId: context.blockId,
          profileId: context.profileId,
          workoutId: draftCommand.identity.workoutId,
        },
        edits: draftEdits,
        updateChangeCount: false,
      };

      mutation.mutate(athleteDraftCommand);
    };

    return makeModifiedPublishedWorkoutLayerMutationFns(
      modifiedPublishedLayers,
      athleteDraftLayers,
      blockDraftLayers,
      workout,
      setsMetric,
      (context, command, draftCommand, callbacks) =>
        runCommands(context, command, draftCommand, callbacks),
    );
  }, [athleteWorkoutLayers, mutation, setsMetric, workout]);

  // Tech Debt: This is only required because of the legacy tablet implementation of CreateEditExerciseSheet, change once v1 tablet is deprecated
  const sectionExercises: SectionExercises[] = useMemo(() => {
    if (!rawWorkout) return [];
    return rawWorkout.sections.map((s) => ({
      sectionId: s.workoutSectionId,
      sectionName: s.name,
      workoutId: rawWorkout.workoutId,
      workoutName: rawWorkout.name ?? '',
      exerciseWorkouts: s.entries.flatMap((entry) => {
        // Convert section entries to ExerciseWorkouts format
        const actualLayer = athleteWorkoutLayers.actual ?? null;
        const exercises = getExercisesFromEntry(entry, actualLayer);
        return exercises.map((exercise) => ({
          libraryExerciseId: exercise.libraryExerciseId,
          exerciseName: exercise.exerciseName,
          workoutExerciseId: exercise.workoutExerciseId,
          section: s,
          exerciseSortOrder: exercise.sortOrder,
          supersetSortOrder:
            entry.__type === 'WorkoutSuperset' ? exercise.sortOrder : undefined,
          workoutSupersetId:
            entry.__type === 'WorkoutSuperset'
              ? entry.workoutSupersetId
              : undefined,
          instruction: exercise.instruction ?? undefined,
          alternateGroup:
            entry.__type === 'WorkoutAlternateGroup' ? entry : undefined,
          isEachSide: exercise.isEachSide,
          context,
          workoutExercise: exercise,
          exerciseBenchmark: exerciseBenchmarks.find(
            (m) =>
              m.libraryExerciseId === exercise.libraryExerciseId &&
              m.profileId === context.profileId,
          ),
          isAlternate: entry.__type === 'WorkoutAlternateGroup',
        }));
      }),
      context,
    }));
  }, [rawWorkout, context, athleteWorkoutLayers.actual, exerciseBenchmarks]);

  const performExerciseSwap = ({
    mutationFns,
    sectionExercises,
    selectedExercise,
    newData,
    targetSectionId,
  }: {
    mutationFns: ModifiedPublishedWorkoutLayerMutationsFns;
    sectionExercises: SectionExercises;
    selectedExercise: WorkoutExercise;
    newData: {
      data: ExerciseMutationData;
      metricsData: MetricData[];
      setData: SetData[];
    };
    targetSectionId: string;
  }) => {
    const { data, metricsData, setData } = newData;

    // Detect exercise context from section data
    const exerciseWorkout = sectionExercises.exerciseWorkouts.find(
      (ew) => ew.workoutExerciseId === selectedExercise.workoutExerciseId,
    );

    // Find the workout section for context detection — use rawWorkout so filters
    // don't hide the section that sectionExercises (derived from rawWorkout) references.
    const section = rawWorkout?.sections.find(
      (s) => s.workoutSectionId === exerciseWorkout?.section.workoutSectionId,
    );

    // Exercise is in a superset
    if (exerciseWorkout?.workoutSupersetId) {
      const superset = section?.entries.find(
        (entry) =>
          entry.__type === 'WorkoutSuperset' &&
          entry.workoutSupersetId === exerciseWorkout.workoutSupersetId,
      ) as WorkoutSuperset | undefined;

      // Exercise is also in an alternate group within the superset
      return mutationFns.onSwapExercise(
        sectionExercises.context,
        sectionExercises.workoutId,
        targetSectionId,
        selectedExercise.workoutExerciseId,
        data,
        metricsData,
        setData,
        superset,
      );
    }

    return mutationFns.onSwapExercise(
      sectionExercises.context,
      sectionExercises.workoutId,
      targetSectionId,
      selectedExercise.workoutExerciseId,
      data,
      metricsData,
      setData,
      undefined,
    );
  };

  const handleSaveExercise = async (exerciseData: ExerciseFormData) => {
    const { exercise } = exerciseData;
    // If there are multiple workouts on the same day for a user, we merge the workouts together
    // we add the exercise to the workout based on the section it is being added to
    const {
      data,
      metricsData,
      setData,
      newWorkoutSection,
      exerciseSortOrderEntry,
    } = parseExerciseData(exerciseData, selectedExercise?.workoutExerciseId);
    if (!newWorkoutSection?.workoutSectionId) return;

    const workoutForSheet = sectionExercises.find(
      (section: SectionExercises) =>
        section.sectionId === newWorkoutSection.workoutSectionId,
    );
    if (!workoutForSheet?.context || !workoutForSheet?.workoutId) return;

    if (selectedExercise) {
      const allModifiedEdits: WorkoutLayerDomain.WorkoutLayerEdit[] = [];
      const allDraftEdits: WorkoutLayerDomain.WorkoutLayerEdit[] = [];

      const metricsEdits = getMetricsEdits(
        modifiedPublishedMutationsFns,
        metricsData,
        selectedExercise.metrics,
        workoutForSheet,
        selectedExercise,
      );
      allModifiedEdits.push(...metricsEdits.modifiedEdits);
      allDraftEdits.push(...metricsEdits.draftEdits);

      const setsEdits = getSetsEdits(
        modifiedPublishedMutationsFns,
        setData,
        workoutForSheet,
        selectedExercise,
      );
      allModifiedEdits.push(...setsEdits.modifiedEdits);
      allDraftEdits.push(...setsEdits.draftEdits);

      const isEachSideEdits = getIsEachSideEdits(
        modifiedPublishedMutationsFns,
        exerciseData.isEachSide,
        workoutForSheet,
        selectedExercise,
      );
      allModifiedEdits.push(...isEachSideEdits.modifiedEdits);
      allDraftEdits.push(...isEachSideEdits.draftEdits);

      // Exercise Swap
      if (exercise.id !== selectedExercise?.libraryExerciseId) {
        const newWorkoutExerciseId = v4();
        const swapEdits = performExerciseSwap({
          mutationFns: modifiedPublishedMutationsFns,
          sectionExercises: workoutForSheet,
          selectedExercise,
          newData: { data, metricsData, setData },
          targetSectionId: newWorkoutSection.workoutSectionId,
        });

        allModifiedEdits.push(...swapEdits.modifiedEdits);
        allDraftEdits.push(...swapEdits.draftEdits);

        const layerIdentity = modifiedPublishedMutationsFns.executeAllEdits(
          workoutForSheet.context,
          workoutForSheet.workoutId,
          allModifiedEdits,
          allDraftEdits,
          {
            onSuccess: () => {
              toast({
                title: t('swappedExerciseMessage', {
                  fromExercise: selectedExercise.exerciseName,
                  toExercise: exercise.name,
                }),
                severity: 'success',
              });
            },
            onError: () => {
              toast({
                title: t('errorSwappingExercise'),
                severity: 'error',
              });
            },
          },
        );

        return Promise.all([
          updateActivityLog.mutateAsync({
            teamId: selectedAthlete.teamId,
            blockId: workoutForSheet.context.blockId,
            athleteId: selectedAthlete.id,
            workoutId: workoutForSheet.workoutId,
            changeType: 'tablet_exercise_updated',
            workoutExerciseId: newWorkoutExerciseId,
            athleteModifiedPublishedLayerId: layerIdentity.id,
            blockLayerId: workoutForSheet.context.blockLayerId ?? '',
            athleteLayerId: workoutForSheet.context.athleteLayerId,
          }),
          updateActivityLog.mutateAsync({
            teamId: selectedAthlete.teamId,
            blockId: workoutForSheet.context.blockId,
            athleteId: selectedAthlete.id,
            workoutId: workoutForSheet.workoutId,
            changeType: 'tablet_exercise_removed',
            workoutExerciseId: selectedExercise.workoutExerciseId,
            athleteModifiedPublishedLayerId: layerIdentity.id,
            blockLayerId: workoutForSheet.context.blockLayerId ?? '',
            athleteLayerId: workoutForSheet.context.athleteLayerId,
          }),
        ]);
      }

      const layerIdentity = modifiedPublishedMutationsFns.executeAllEdits(
        workoutForSheet.context,
        workoutForSheet.workoutId,
        allModifiedEdits,
        allDraftEdits,
        {
          onSuccess: () => {
            toast({
              title: t('updatedExercise', { exerciseName: exercise.name }),
              severity: 'success',
            });
          },
          onError: () => {
            toast({
              title: t('errorUpdatingExercise'),
              severity: 'error',
            });
          },
        },
      );
      return updateActivityLog.mutate({
        teamId: selectedAthlete.teamId,
        blockId: workoutForSheet.context.blockId,
        athleteId: selectedAthlete.id,
        workoutId: workoutForSheet.workoutId,
        changeType: 'tablet_prescription_change',
        workoutExerciseId: selectedExercise.workoutExerciseId,
        athleteModifiedPublishedLayerId: layerIdentity.id,
        blockLayerId: workoutForSheet.context.blockLayerId ?? '',
        athleteLayerId: workoutForSheet.context.athleteLayerId,
      });
    } else {
      const workoutExerciseId = v4();

      const supersetSortOrder = exerciseSortOrderEntry?.supersetSortOrder;
      const workoutSupersetId = exerciseSortOrderEntry?.workoutSupersetId;
      const isAddingToSuperset =
        supersetSortOrder !== undefined && workoutSupersetId !== undefined;

      const exerciseMutationData = isAddingToSuperset
        ? { ...data, sortOrder: supersetSortOrder }
        : data;

      const { modifiedEdits, draftEdits } =
        modifiedPublishedMutationsFns.onAddExercise(
          workoutForSheet.context,
          workoutForSheet.workoutId,
          newWorkoutSection.workoutSectionId,
          exerciseMutationData,
          metricsData,
          setData,
          workoutSupersetId,
          workoutExerciseId,
        );

      const layerIdentity = modifiedPublishedMutationsFns.executeAllEdits(
        workoutForSheet.context,
        workoutForSheet.workoutId,
        modifiedEdits,
        draftEdits,
        {
          onSuccess: () => {
            toast({
              title:
                exerciseSortOrderEntry?.supersetSortOrder &&
                exerciseSortOrderEntry.workoutSupersetId
                  ? t('addedExerciseToSuperset', { exercise: exercise.name })
                  : t('addedExercise', { exercise: exercise.name }),
              severity: 'success',
            });
          },
          onError: () => {
            toast({
              title: t('errorAddingExercise'),
              severity: 'error',
            });
          },
        },
      );

      return updateActivityLog.mutate({
        teamId: selectedAthlete.teamId,
        blockId: workoutForSheet.context.blockId,
        athleteId: selectedAthlete.id,
        workoutId: workoutForSheet.workoutId,
        changeType: 'tablet_exercise_added',
        workoutExerciseId: workoutExerciseId,
        athleteModifiedPublishedLayerId: layerIdentity.id,
        blockLayerId: workoutForSheet.context.blockLayerId ?? '',
        athleteLayerId: workoutForSheet.context.athleteLayerId,
      });
    }
  };

  const handleSaveNote = (
    athleteId: string,
    workoutId: string,
    exerciseId: string,
    note: string,
    workoutExerciseId: string,
  ) => {
    addExerciseNote.mutate(
      {
        note,
        teamId: selectedAthlete.teamId,
        workoutId,
        athleteId,
        exerciseId,
        workoutExerciseId,
      },
      {
        onSuccess: () => {
          toast({
            title: t('noteAdded'),
            severity: 'success',
          });
        },
        onError: () => {
          toast({
            title: t('errorAddingNote'),
            severity: 'error',
          });
        },
      },
    );
    handleCloseAddNoteExerciseDrawer();
  };

  const handleCloseAddExerciseDrawer = useCallback(() => {
    setAddExerciseDrawerOpen(false);
    setSelectedExercise(undefined);
  }, []);

  const handleOpenAddExerciseDrawer = useCallback(
    (exercise?: WorkoutExercise) => {
      const changeCount =
        athleteWorkoutLayers.draft?.unpublishedChangeCount ?? 0;
      if (changeCount > 0) {
        toast({
          title: t('cannotEditExercise'),
          severity: 'warning',
        });

        return;
      }
      setAddExerciseDrawerOpen(true);
      setSelectedExercise(exercise);
    },
    [athleteWorkoutLayers.draft, t],
  );

  const handleOpenAddNoteExerciseDrawer = useCallback(
    (exercise?: WorkoutExercise) => {
      setAddNoteExerciseDrawerOpen(true);
      setSelectedExercise(exercise);
    },
    [],
  );

  const handleCloseAddNoteExerciseDrawer = useCallback(() => {
    setAddNoteExerciseDrawerOpen(false);
    setSelectedExercise(undefined);
  }, []);

  const handleOpenExerciseHistoryDrawer = useCallback(
    (exercise?: WorkoutExercise) => {
      setExerciseHistoryDrawerOpen(true);
      setSelectedExercise(exercise);
    },
    [],
  );

  const handleCloseExerciseHistoryDrawer = useCallback(() => {
    setExerciseHistoryDrawerOpen(false);
    setSelectedExercise(undefined);
  }, []);

  const handleOpenCompleteWorkoutDialog = useCallback(() => {
    setCompleteWorkoutDrawerOpen(true);
  }, []);

  const handleCloseCompleteWorkoutDialog = useCallback(() => {
    setCompleteWorkoutDrawerOpen(false);
  }, []);

  const handleCompleteWorkout = useCallback(() => {
    if (!rawWorkout) return;
    const command: PublishedWorkoutStatusDomain.Command = {
      module: PublishedWorkoutStatusDomain.Module,
      type: 'UpdateAthleteWorkoutStatus',
      input: {
        blockId: context.blockId,
        workoutId: rawWorkout.workoutId,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        updates: [
          {
            profileId: selectedAthlete.id,
            status: 'completed',
            workout: rawWorkout,
          },
        ],
      },
    };
    actualMutationFns.onChangeWorkoutStatus(
      context,
      rawWorkout.workoutId,
      rawWorkout,
      'completed',
    );
    mutation.mutate(command);
    handleCloseCompleteWorkoutDialog();
    onSelectWorkout(undefined, undefined, selectedAthlete.id);

    // Find remaining athletes (excluding current one)
    const remainingAthletes = selectedAthletes.filter(
      (athlete) => athlete.id !== selectedAthlete.id,
    );

    // Remove the current athlete from session and list
    removeAthleteFromSelectedAthletes(selectedAthlete.id);

    const nextAthlete = remainingAthletes[0];
    if (!nextAthlete) {
      // No more athletes, exit the session
      onExit();
    } else {
      if (splitViewEnabled && canShowSplitView) {
        setActiveAthletes(remainingAthletes);
      } else {
        setActiveAthletes([nextAthlete]);
      }
    }

    toast({
      title: t('workoutCompleted'),
      severity: 'success',
    });
  }, [
    context,
    rawWorkout,
    selectedAthlete.id,
    actualMutationFns,
    mutation,
    handleCloseCompleteWorkoutDialog,
    onSelectWorkout,
    selectedAthletes,
    removeAthleteFromSelectedAthletes,
    t,
    onExit,
    splitViewEnabled,
    canShowSplitView,
    setActiveAthletes,
  ]);

  const hasNextSection = useMemo(() => {
    const selectedSectionId = getSelectedSectionId(selectedAthlete.id);
    if (!rawWorkout || !workout || !selectedSectionId) return false;
    // Use rawWorkout to find the current position so filtered-out sections don't
    // cause findIndex to return -1 and incorrectly disable the button.
    const rawIdx = rawWorkout.sections.findIndex(
      (section) => section.workoutSectionId === selectedSectionId,
    );
    if (rawIdx === -1) return false;
    // There must be a visible (non-filtered) section after the current position.
    const visibleSectionIds = new Set(
      workout.sections.map((s) => s.workoutSectionId),
    );
    return rawWorkout.sections
      .slice(rawIdx + 1)
      .some((s) => visibleSectionIds.has(s.workoutSectionId));
  }, [getSelectedSectionId, selectedAthlete.id, rawWorkout, workout]);

  const handleNextSection = useCallback(() => {
    const selectedSectionId = getSelectedSectionId(selectedAthlete.id);
    if (!hasNextSection || !rawWorkout || !workout || !selectedSectionId)
      return;
    const rawIdx = rawWorkout.sections.findIndex(
      (section) => section.workoutSectionId === selectedSectionId,
    );
    if (rawIdx === -1) return;
    const visibleSectionIds = new Set(
      workout.sections.map((s) => s.workoutSectionId),
    );
    // Find the next section after the current position that is visible (not filtered out).
    const nextSection = rawWorkout.sections
      .slice(rawIdx + 1)
      .find((s) => visibleSectionIds.has(s.workoutSectionId));
    if (nextSection) {
      onSelectSection(nextSection.workoutSectionId, selectedAthlete.id);
    }
  }, [
    hasNextSection,
    rawWorkout,
    workout,
    getSelectedSectionId,
    selectedAthlete.id,
    onSelectSection,
  ]);

  const handleEditSet = useCallback(
    (
      workoutId: string,
      workoutSetId: string,
      exercise: WorkoutExercise,
      data: SetMutationData,
    ) => {
      return actualMutationFns.onEditSetStatus(
        context,
        workoutId,
        workoutSetId,
        exercise,
        data,
      );
    },
    [actualMutationFns, context],
  );

  const handleEditExerciseStatus = useCallback(
    (
      workoutId: string,
      workoutExerciseId: string,
      sets: WorkoutSet[],
      data: ExerciseMutationData,
    ) => {
      actualMutationFns.onExerciseStatusEdit(
        context,
        workoutId,
        workoutExerciseId,
        sets,
        data,
      );
    },
    [actualMutationFns, context],
  );

  const contextValue = useMemo(
    () => ({
      selectedAthlete,
      workout,
      originalWorkout,
      exerciseLabelMap,
      exerciseVideoMap,
      applyBenchmarkData,
      getBenchmarkGrid,
      defaultWeightUnit,
      getUnitsForMetric,
      getMetric,
      actualLayer: athleteWorkoutLayers.actual ?? null,
      blockType: athleteWorkoutLayers.block?.blockType,
      context,
      modifiedPublishedMutationsFns,
      actualMutationFns,
      sectionExercises,
      filters,
      hasNextSection,
      handleNextSection,
      handleOpenAddExerciseDrawer,
      handleOpenAddNoteExerciseDrawer,
      handleOpenExerciseHistoryDrawer,
      handleOpenCompleteWorkoutDialog,
      handleCompleteWorkout,
      handleEditSet,
      handleEditExerciseStatus,
      unrecordedActuals,
    }),
    [
      selectedAthlete,
      workout,
      originalWorkout,
      exerciseLabelMap,
      exerciseVideoMap,
      applyBenchmarkData,
      getBenchmarkGrid,
      defaultWeightUnit,
      getUnitsForMetric,
      getMetric,
      athleteWorkoutLayers.actual,
      athleteWorkoutLayers.block?.blockType,
      context,
      modifiedPublishedMutationsFns,
      actualMutationFns,
      sectionExercises,
      filters,
      hasNextSection,
      handleNextSection,
      handleOpenAddExerciseDrawer,
      handleOpenAddNoteExerciseDrawer,
      handleOpenExerciseHistoryDrawer,
      handleOpenCompleteWorkoutDialog,
      handleCompleteWorkout,
      handleEditSet,
      handleEditExerciseStatus,
      unrecordedActuals,
    ],
  );

  return (
    <WorkoutContext.Provider value={contextValue}>
      {children}
      <TabletCreateEditExerciseSheet
        open={addExerciseDrawerOpen}
        onSave={(exerciseData) => handleSaveExercise(exerciseData)}
        onClose={handleCloseAddExerciseDrawer}
        athlete={selectedAthlete}
        editExercise={selectedExercise}
        workouts={sectionExercises}
      />
      <TabletExerciseNoteModal
        athlete={selectedAthlete}
        workout={{
          workoutId: rawWorkout?.workoutId ?? '',
          workoutName: rawWorkout?.name ?? '',
        }}
        exercise={selectedExercise}
        open={!!addNoteExerciseDrawerOpen}
        onClose={handleCloseAddNoteExerciseDrawer}
        onSave={handleSaveNote}
      />
      <ExerciseHistoryDrawer
        athlete={selectedAthlete}
        exercise={selectedExercise}
        open={!!exerciseHistoryDrawerOpen}
        onClose={handleCloseExerciseHistoryDrawer}
      />
      <CompleteWorkoutDialog
        athlete={selectedAthlete}
        teamId={selectedAthlete.teamId}
        workout={rawWorkout}
        open={completeWorkoutDrawerOpen}
        onClose={handleCloseCompleteWorkoutDialog}
        onComplete={handleCompleteWorkout}
        unrecordedActuals={unrecordedActuals}
      />
    </WorkoutContext.Provider>
  );
};

export const useWorkoutContext = (): WorkoutContextValue => {
  const context = useContext(WorkoutContext);
  if (!context) {
    throw new Error('useWorkoutContext must be used within a WorkoutProvider');
  }
  return context;
};
